<script setup>
import { computed, watch, ref } from "vue";

const props = defineProps({
    gridData: {
        type: Array,
        required: true,
    },
});

const justRevealedCells = ref(new Set());

const isHorizontal = (direction) => direction === "H";

// Вычисляем размер сетки на основе координат
const gridSize = computed(() => {
    let maxRow = 0;
    let maxCol = 0;
    let minRow = Infinity;
    let minCol = Infinity;

    props.gridData.forEach(({ y, x, direction, length }) => {
        minRow = Math.min(minRow, y);
        minCol = Math.min(minCol, x);
        if (isHorizontal(direction)) {
            maxRow = Math.max(maxRow, y);
            maxCol = Math.max(maxCol, x + length - 1);
        } else {
            maxRow = Math.max(maxRow, y + length - 1);
            maxCol = Math.max(maxCol, x);
        }
    });

    return {
        rows: maxRow - minRow + 1,
        cols: maxCol - minCol + 1,
        minRow,
        minCol,
    };
});

const rows = computed(() => gridSize.value.rows);
const cols = computed(() => gridSize.value.cols);

// Строим сетку
const grid = computed(() => {
    const cells = [];
    const r = rows.value;
    const c = cols.value;
    const offsetRow = gridSize.value.minRow - 1;
    const offsetCol = gridSize.value.minCol - 1;

    for (let i = 0; i < r * c; i++) {
        cells.push({ letter: null, revealed: false, justRevealed: false, hasWord: false });
    }

    props.gridData.forEach((item) => {
        const word = item.word || null;

        for (let i = 0; i < item.length; i++) {
            let cellRow = item.y - 1 - offsetRow;
            let cellCol = item.x - 1 - offsetCol;

            if (isHorizontal(item.direction)) {
                cellCol = item.x - 1 - offsetCol + i;
            } else {
                cellRow = item.y - 1 - offsetRow + i;
            }

            const index = cellRow * c + cellCol;
            if (index >= 0 && index < cells.length) {
                cells[index].hasWord = true;

                if (word) {
                    cells[index].letter = word[i]?.toUpperCase() || null;
                    cells[index].revealed = true;
                    cells[index].justRevealed = justRevealedCells.value.has(`${cellRow}-${cellCol}`);
                }
            }
        }
    });

    return cells;
});

// Анимация при нахождении нового слова
watch(
    () => props.gridData.map((item) => item.word || null),
    (newWords, oldWords) => {
        if (!oldWords) return;

        const offsetRow = gridSize.value.minRow - 1;
        const offsetCol = gridSize.value.minCol - 1;

        props.gridData.forEach((item, itemIndex) => {
            const isNewlyFound = newWords[itemIndex] && !oldWords[itemIndex];
            if (!isNewlyFound) return;

            for (let i = 0; i < item.length; i++) {
                let cellRow = item.y - 1 - offsetRow;
                let cellCol = item.x - 1 - offsetCol;

                if (isHorizontal(item.direction)) {
                    cellCol = item.x - 1 - offsetCol + i;
                } else {
                    cellRow = item.y - 1 - offsetRow + i;
                }

                const key = `${cellRow}-${cellCol}`;
                justRevealedCells.value.add(key);

                setTimeout(() => {
                    justRevealedCells.value.delete(key);
                }, 600);
            }
        });
    },
    { deep: true }
);
</script>
<template>
    <div class="board-container">
        <div
            class="board"
            :style="{
                gridTemplateColumns: `repeat(${cols}, var(--cell-size))`,
                gridTemplateRows: `repeat(${rows}, var(--cell-size))`,
            }"
        >
            <div
                v-for="(cell, index) in grid"
                :key="index"
                class="cell"
                :class="{
                    empty: !cell.hasWord,
                    revealed: cell.revealed,
                    'just-revealed': cell.justRevealed,
                }"
            >
                <span v-if="cell.letter && cell.revealed">{{ cell.letter }}</span>
            </div>
        </div>
    </div>
</template>
<style lang="scss" scoped>
@use '@/assets/scss/base/vars';
.board-container {
    --cell-size: 4.8rem;

    @media (max-width: vars.$x480) {
        --cell-size: 2.8rem;
    }
}

.board {
    display: grid;
    gap: 0.4rem;
    @media (max-width: vars.$mn) {
        margin-left: auto;
        margin-right: auto;
    }
}

.cell {
    width: var(--cell-size);
    height: var(--cell-size);
    display: flex;
    align-items: center;
    justify-content: center;
    font-family: var(--second-family);
    font-weight: 700;
    font-size: 2.8rem;
    line-height: 110%;
    text-transform: uppercase;
    text-align: center;
    border-radius: 1.2rem;
    transition: all 0.3s ease;

    @media (max-width: vars.$x480) {
        border-radius: 0.6rem;
        font-size: 2rem;
    }

    &.empty {
        background: transparent;
    }

    &:not(.empty) {
        background: rgba(255, 255, 255, 0.1);
        border: 1px solid var(--border-accent-2-1);
        color: transparent;
    }

    &.revealed {
        color: var(--text-inverse-100);
    }

    &.just-revealed {
        animation: reveal 0.5s ease-out;
    }
}

@keyframes reveal {
    0% {
        transform: scale(0.5) rotateY(90deg);
        opacity: 0;
    }
    50% {
        transform: scale(1.2) rotateY(0deg);
    }
    100% {
        transform: scale(1) rotateY(0deg);
        opacity: 1;
    }
}
</style>
